import { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from 'utils/@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { useInjectReducer } from 'utils/redux-injectors';
import { baseQuery, endpoints } from 'utils/api/endpoints';
import { State } from './types';

// Transform API data to frontend format
const transformTeamData = (apiData: any) => {
  if (!apiData) return [];

  return apiData.map((item: any) => {
    // Handle skills - they might come as JSON string or array
    let skills = [];
    if (item.skills) {
      if (typeof item.skills === 'string') {
        try {
          skills = JSON.parse(item.skills);
        } catch (error) {
          console.warn('Failed to parse skills JSON:', item.skills);
          skills = [];
        }
      } else if (Array.isArray(item.skills)) {
        skills = item.skills;
      }
    }

    return {
      team_id: item.team_id?.toString() || item.id?.toString(),
      name: item.name,
      email: item.email,
      phone: item.phone,
      designation: item.designation,
      photo: item.photo,
      location: item.address
        ? `${item.address}${item.country ? `, ${item.country}` : ''}`
        : item.country,
      website: item.social_links?.find(
        (link: any) => link.platform === 'website',
      )?.url,
      linkedin: item.social_links?.find(
        (link: any) => link.platform === 'linkedin',
      )?.url,
      twitter: item.social_links?.find(
        (link: any) => link.platform === 'twitter',
      )?.url,
      instagram: item.social_links?.find(
        (link: any) => link.platform === 'instagram',
      )?.url,
      whatsapp: item.social_links?.find(
        (link: any) => link.platform === 'whatsapp',
      )?.url,
      telegram: item.social_links?.find(
        (link: any) => link.platform === 'telegram',
      )?.url,
      bio: item.bio,
      created_at: item.created_at || item.join_date,
      updated_at: item.updated_at,
      skills: skills,
      status: item.status === 1 || item.status === true,
      position: item.position,
      gender: item.gender,
      address: item.address,
      country: item.country,
      video: item.video,
      social_links: item.social_links || [],
    };
  });
};

// Teams response interface
export interface TeamsResponse {
  message: string;
  status: string;
  data: any[];
  pagination?: {
    current_page: number;
    total_pages: number;
    total_items: number;
    items_per_page: number;
    has_next: boolean;
    has_prev: boolean;
  };
}

export const initialState: State = {
  selectedTeam: null,
};

const slice = createSlice({
  name: 'teams',
  initialState,
  reducers: {
    setSelectedTeam: (state, action: PayloadAction<any>) => {
      state.selectedTeam = action.payload;
    },
  },
});

export const api = createApi({
  reducerPath: 'teamsApi',
  baseQuery: baseQuery,
  tagTypes: ['Teams'],
  endpoints: builder => ({
    getTeams: builder.query<
      TeamsResponse,
      { page?: number; limit?: number; search?: string }
    >({
      query: params => ({
        url: endpoints.TEAMS,
        method: 'GET',
        params: {
          page: params.page || 1,
          limit: params.limit || 10,
          search: params.search || '',
        },
      }),
      transformResponse: (response: any) => {
        // The baseQuery wraps the actual API response in result.data
        const apiResponse = response.data || response;

        // Handle different response structures
        let data = apiResponse.data;
        let pagination = apiResponse.pagination;

        // If response is wrapped in a data property
        if (apiResponse.data && apiResponse.data.data) {
          data = apiResponse.data.data;
          pagination = apiResponse.data.pagination;
        }

        // Fallback: if data is directly in the response
        if (!data && Array.isArray(apiResponse)) {
          data = apiResponse;
        }

        const transformedData = transformTeamData(data);

        return {
          message: apiResponse.message || 'Success',
          status: apiResponse.status || 'success',
          data: transformedData,
          pagination: pagination || {
            current_page: 1,
            total_pages: 1,
            total_items: transformedData.length,
            items_per_page: transformedData.length,
            has_next: false,
            has_prev: false,
          },
        };
      },
      providesTags: ['Teams'],
    }),

    addTeam: builder.mutation<any, any>({
      query: data => ({
        url: endpoints.TEAMS + '/add',
        method: 'POST',
        body: {
          name: data.name,
          designation: data.designation,
          email: data.email,
          phone: data.phone,
          gender: data.gender,
          bio: data.bio,
          address: data.address,
          country: data.country,
          status: data.status ? 1 : 0,
          photo: data.photo,
          video: data.video,
          social_links: [
            ...(data.website
              ? [{ platform: 'website', url: data.website }]
              : []),
            ...(data.linkedin
              ? [{ platform: 'linkedin', url: data.linkedin }]
              : []),
            ...(data.twitter
              ? [{ platform: 'twitter', url: data.twitter }]
              : []),
            ...(data.instagram
              ? [{ platform: 'instagram', url: data.instagram }]
              : []),
            ...(data.whatsapp
              ? [{ platform: 'whatsapp', url: data.whatsapp }]
              : []),
            ...(data.telegram
              ? [{ platform: 'telegram', url: data.telegram }]
              : []),
          ],
          skills: data.skills || [],
        },
      }),
      invalidatesTags: ['Teams'],
    }),

    updateTeam: builder.mutation<any, { id: string; data: any }>({
      query: ({ id, data }) => ({
        url: `${endpoints.TEAMS}/update/${id}`,
        method: 'PUT',
        body: {
          name: data.name,
          designation: data.designation,
          email: data.email,
          phone: data.phone,
          gender: data.gender,
          bio: data.bio,
          address: data.address,
          country: data.country,
          status: data.status ? 1 : 0,
          photo: data.photo,
          video: data.video,
          social_links: [
            ...(data.website
              ? [{ platform: 'website', url: data.website }]
              : []),
            ...(data.linkedin
              ? [{ platform: 'linkedin', url: data.linkedin }]
              : []),
            ...(data.twitter
              ? [{ platform: 'twitter', url: data.twitter }]
              : []),
            ...(data.instagram
              ? [{ platform: 'instagram', url: data.instagram }]
              : []),
            ...(data.whatsapp
              ? [{ platform: 'whatsapp', url: data.whatsapp }]
              : []),
            ...(data.telegram
              ? [{ platform: 'telegram', url: data.telegram }]
              : []),
          ],
          skills: data.skills || [],
        },
      }),
      invalidatesTags: ['Teams'],
    }),

    deleteTeam: builder.mutation<any, string>({
      query: id => ({
        url: `${endpoints.TEAMS}/delete/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Teams'],
    }),

    updateTeamPositions: builder.mutation<
      any,
      Array<{ id: string; position: number }>
    >({
      query: positions => ({
        url: endpoints.TEAMS_POSITIONS,
        method: 'PUT',
        body: { positions },
      }),
      invalidatesTags: ['Teams'],
    }),
  }),
});

export const { actions: teamsAction } = slice;

export const useTeamsSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectReducer({ key: api.reducerPath, reducer: api.reducer });
  return {
    useGetTeamsQuery: api.useLazyGetTeamsQuery,
    useAddTeamMutation: api.useAddTeamMutation,
    useUpdateTeamMutation: api.useUpdateTeamMutation,
    useDeleteTeamMutation: api.useDeleteTeamMutation,
    useUpdateTeamPositionsMutation: api.useUpdateTeamPositionsMutation,
  };
};
